import { ComponentChild, FunctionalComponent, h } from 'preact';
import ExpandableCard, { ToggleProps } from '../ExpandableCard/ExpandableCard';
import { PurposeProps, SpecialFeatureProps, StackProps } from '../TCFHandler';
import { RefContainer, RowDescription, ToggleRow } from './VirtualList.style';
import { TCFFeature, isTcfLabels, isTcfUI } from '@usercentrics/cmp-browser-sdk';
import { InlineToggle } from '../Toggles';
import LegalDescriptionFormatter from '../LegalDescriptionFormatter/LegalDescriptionFormatter';
import { TabPanelTitle } from '../Tabs';
import { memo } from 'preact/compat';
import { nullishOperation } from '../../App.helpers';
import { useRef } from 'preact/hooks';
import { useSettingsContext } from '../../contexts/SettingsContext';
import { useVirtualListItem } from '../../hooks';

type Data = TCFFeature | PurposeProps | SpecialFeatureProps | StackProps;

interface ItemProps<T> {
  data: T[];
  index: number;
  id: number;
  getItemSize: (index: number) => number;
  setItemSize: (index: number, height: number) => void;
  testid: string;
  title?: string;
  titleAriaLevel: number;
  useInlineToggle?: boolean;
  useFromStackOnly?: boolean;
}

const VirtualLegalDescriptionItem: FunctionalComponent<ItemProps<Data>> = memo(
  ({
    data,
    id,
    index,
    getItemSize,
    setItemSize,
    testid,
    title,
    useInlineToggle = false,
    useFromStackOnly = false,
    titleAriaLevel,
  }) => {
    const itemRef = useRef<HTMLDivElement>(null);
    const { updateComponentSize } = useVirtualListItem({ getItemSize, index, itemRef, setItemSize });
    const { labels, ui } = useSettingsContext();

    if (!labels || !isTcfLabels(labels) || !ui || !isTcfUI(ui)) {
      return null;
    }

    const shouldShowToggles = (useFromStackOnly && !ui.firstLayer.hideToggles) || !useFromStackOnly;
    const dataId = title != null ? id - 1 : id;

    if (title != null && id === 0) {
      return (
        <RefContainer ref={itemRef} tabIndex={-1}>
          <TabPanelTitle tabIndex={-1} role="heading" aria-level={titleAriaLevel}>
            {title}
          </TabPanelTitle>
        </RefContainer>
      );
    }

    const item = data[dataId];

    if (!item) {
      return null;
    }

    const getToggles = (
      ariaLabel: string,
      checked: boolean,
      toggleId: string,
      label: string,
      onToggle: () => void,
    ): ToggleProps[] => [
      {
        ariaLabel,
        checked,
        id: toggleId,
        label,
        onToggle,
      },
    ];

    const renderItem = (): ComponentChild => {
      // render a stack
      if ((item as StackProps).stack && isTcfUI(ui)) {
        const stackProps = item as StackProps;

        if (useInlineToggle) {
          return (
            <ToggleRow data-testid="uc-tcf-stacks">
              <RowDescription id={`${stackProps.stack.name}-description-id`}>{stackProps.stack.name}</RowDescription>
              {!ui.firstLayer.hideToggles && (
                <InlineToggle
                  labelledBy={`${stackProps.stack.name}-description-id`}
                  checked={stackProps.checked}
                  id={stackProps.stack.id}
                  onToggle={stackProps.onStackConsentToggle}
                />
              )}
            </ToggleRow>
          );
        }

        const toggles: ToggleProps[] = [];

        if (shouldShowToggles) {
          getToggles(
            `${stackProps.stack.name} ${nullishOperation(title, 'stack')} ${labels.ariaLabels.vendorConsentToggle}`,
            stackProps.checked,
            `stackConsent${stackProps.stack.id}`,
            '',
            stackProps.onStackConsentToggle,
          );
        }

        return (
          <ExpandableCard
            ariaLevel={titleAriaLevel + 1}
            hideToggle={!shouldShowToggles}
            onToggleExpandable={updateComponentSize}
            testid={testid}
            title={stackProps.stack.name}
            toggles={toggles}
            useBoldTitle={!useFromStackOnly}
          >
            <LegalDescriptionFormatter ariaLevel={titleAriaLevel + 2} description={stackProps.stack.description} />
          </ExpandableCard>
        );
      }

      // render specialPurpose and feature
      if ((item as TCFFeature).name) {
        const basePurpose = item as TCFFeature;

        if (useInlineToggle) {
          return (
            <ToggleRow data-testid="uc-tcf-special-purposes">
              <RowDescription>{basePurpose.name}</RowDescription>
            </ToggleRow>
          );
        }

        return (
          <ExpandableCard
            ariaLevel={titleAriaLevel + 1}
            testid={testid}
            hideToggle={false}
            title={basePurpose.name}
            onToggleExpandable={updateComponentSize}
            useBoldTitle={!useFromStackOnly}
          >
            <LegalDescriptionFormatter
              ariaLevel={titleAriaLevel + 2}
              description={basePurpose.description}
              legalDescription={useFromStackOnly ? '' : basePurpose.descriptionLegal}
            />
          </ExpandableCard>
        );
      }

      // Render a purpose
      if ((item as PurposeProps).purpose && isTcfUI(ui)) {
        const purposeProps = item as PurposeProps;

        if (useInlineToggle) {
          return (
            <ToggleRow data-testid="uc-tcf-purposes">
              <RowDescription id={`${purposeProps.purpose.name}-description-id`}>
                {purposeProps.purpose.name}
              </RowDescription>
              {!ui.firstLayer.hideToggles && (
                <InlineToggle
                  labelledBy={`${purposeProps.purpose.name}-description-id`}
                  checked={purposeProps.checked}
                  id={purposeProps.purpose.id}
                  onToggle={purposeProps.onPurposeConsentToggle}
                />
              )}
            </ToggleRow>
          );
        }

        let toggles: ToggleProps[] = [];

        if (shouldShowToggles && purposeProps.purpose.showConsentToggle && isTcfLabels(labels)) {
          toggles = getToggles(
            `${purposeProps.purpose.name} ${nullishOperation(title, 'purposes')} ${
              labels.ariaLabels.vendorConsentToggle
            }`,
            purposeProps.checked,
            `purposeConsent${purposeProps.purpose.id}`,
            useFromStackOnly ? '' : labels.toggles.consent,
            purposeProps.onPurposeConsentToggle,
          );
        }

        if (purposeProps.purpose.showLegitimateInterestToggle && !useFromStackOnly && isTcfLabels(labels)) {
          toggles.unshift({
            ariaLabel: `${purposeProps.purpose.name} ${nullishOperation(title, 'purposes')} ${
              labels.ariaLabels.vendorLegIntToggle
            }`,
            checked: purposeProps.legitimateInterestChecked,
            id: `purposeLegitimateInterestConsent${purposeProps.purpose.id}`,
            label: labels.toggles.legitimateInterest,
            onToggle: purposeProps.onPurposeLegitimateInterestToggle,
          });
        }

        return (
          <ExpandableCard
            ariaLevel={titleAriaLevel + 1}
            hideToggle={false}
            onToggleExpandable={updateComponentSize}
            testid={testid}
            title={purposeProps.purpose.name}
            toggles={toggles}
            useBoldTitle={!useFromStackOnly}
          >
            <LegalDescriptionFormatter
              ariaLevel={titleAriaLevel + 2}
              description={purposeProps.purpose.description}
              legalDescription={useFromStackOnly ? '' : purposeProps.purpose.descriptionLegal}
            />
          </ExpandableCard>
        );
      }

      // render a specialFeature
      if ((item as SpecialFeatureProps).specialFeature) {
        const specialFeatureProps = item as SpecialFeatureProps;

        if (useInlineToggle) {
          return (
            <ToggleRow data-testid="uc-tcf-special-features">
              <RowDescription id={`${specialFeatureProps.specialFeature.name}-description-id`}>
                {specialFeatureProps.specialFeature.name}
              </RowDescription>
              {!ui.firstLayer.hideToggles && (
                <InlineToggle
                  labelledBy={`${specialFeatureProps.specialFeature.name}-description-id`}
                  checked={specialFeatureProps.checked}
                  id={specialFeatureProps.specialFeature.id}
                  onToggle={specialFeatureProps.onSpecialFeatureToggle}
                />
              )}
            </ToggleRow>
          );
        }

        let toggles: ToggleProps[] = [];

        if (shouldShowToggles) {
          toggles = getToggles(
            `${specialFeatureProps.specialFeature.name} ${nullishOperation(title, 'features')} ${
              labels.ariaLabels.vendorConsentToggle
            }`,
            specialFeatureProps.checked,
            `specialFeature${specialFeatureProps.specialFeature.id}`,
            specialFeatureProps.checked
              ? labels.toggles.specialFeaturesToggle.onLabel
              : labels.toggles.specialFeaturesToggle.offLabel,
            specialFeatureProps.onSpecialFeatureToggle,
          );
        }

        return (
          <ExpandableCard
            ariaLevel={titleAriaLevel + 1}
            hideToggle={!shouldShowToggles}
            onToggleExpandable={updateComponentSize}
            testid={testid}
            title={specialFeatureProps.specialFeature.name}
            toggles={toggles}
            useBoldTitle={!useFromStackOnly}
          >
            <LegalDescriptionFormatter
              ariaLevel={titleAriaLevel + 2}
              description={specialFeatureProps.specialFeature.description}
              legalDescription={specialFeatureProps.specialFeature.descriptionLegal}
            />
          </ExpandableCard>
        );
      }

      return null;
    };

    return <RefContainer ref={itemRef}>{renderItem()}</RefContainer>;
  },
);

export default VirtualLegalDescriptionItem;
